import { Component, inject, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { PresenceDataTableComponent } from '@/components/shared/presence-data-table/presence-data-table.component';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { MeetingService } from '../../meeting.service';
import { CommonModule } from '@angular/common';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { MessageService } from 'primeng/api';
import { fileUtils } from '@/utils/file.utils';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { BehaviorSubject, Observable } from 'rxjs';
import { WithMetadata } from '@/types';
import { MeetingHistory } from '@/types/meeting.types';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { getSnippetsDataTableStructure } from '../../meeting-convene/utils/extraits-pv-meeting-drawer-object';
import { DataTableAction, DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';

@Component({
  selector: 'sm-history-details',
  imports: [
    PageHeaderComponent,
    PresenceDataTableComponent,
    PreviewPdfComponent,
    TranslateModule,
    CommonModule,
    SpinnerComponent,
    DataTableBuilderComponent,
  ],
  templateUrl: './history-details.component.html',
  styleUrl: './history-details.component.scss',
})
export class HistoryDetailsComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  meetingService = inject(MeetingService);
  meetingRepository = inject(MeetingRepository);
  fileService = inject(FileService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);

  meetingId!: string | null;
  ppmId!: string | null;
  dacId!: string | null;

  breadcrumb = [
    {
      label: 'meeting.breadcrumb.meeting',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.breadcrumb.meetingsList',
      routerLink: ['/meetings', 'list'],
    },
    {
      label: 'meeting.breadcrumb.history',
      routerLink: ['/meetings-history'],
    },
    {
      label: 'meeting.breadcrumb.historyDetails',
      routerLink: ['/meetings-history', 'details'],
    },
  ];

  data!: WithMetadata<MeetingHistory>;
  noData = false;
  loading = false;
  verbalProcessFileSrc: BehaviorSubject<string> = new BehaviorSubject<string>('');

  rowActionsFn: (target: any) => DataTableRowActions = (target: any) => {
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};
    additionalActions['snippetsActions'] = [
      {
        label: 'meeting.snippets.table.actions.inspect',
        icon: 'pi pi-eye',
        action: () =>
          this.fileService.openFile({
            ...target.doc,
            extension: target.doc.fileExtension,
          }),
      },
    ];

    return {
      additionalActions,
    };
  };

  dataTableObject: DynamicDataTable = getSnippetsDataTableStructure({
    comType: '',
    store: this.meetingRepository,
    rowActionsFn: this.rowActionsFn,
  });

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(params => {
      this.meetingId = params.get('id');
      this.ppmId = params.get('ppmId');
      this.dacId = params.get('dacId');
    });
    if (this.meetingId) {
      let result: Observable<WithMetadata<MeetingHistory>> | undefined;
      if (this.dacId) result = this.meetingService.getMeetingHistoryDetails(this.meetingId, this.dacId, 'dac');
      if (this.ppmId) result = this.meetingService.getMeetingHistoryDetails(this.meetingId, this.ppmId, 'ppm');
      this.loading = true;
      if (result) {
        result.subscribe({
          next: response => {
            this.dataTableObject = getSnippetsDataTableStructure({
              comType: response.comType,
              store: this.meetingRepository,
              rowActionsFn: this.rowActionsFn,
            });
            this.loading = false;
            this.data = response;
            this.noData = Object.keys(response || {}).length === 0;
            this.fileService
              .getUrlFromAlfrescoObject(
                {
                  ...response.processVerbal?.document.doc,
                },
                NAMESPACE_ENUM.PV
              )
              .subscribe(blobData => {
                this.verbalProcessFileSrc.next(
                  URL.createObjectURL(
                    new Blob([blobData as BlobPart], { type: fileUtils.getMimeType(response.processVerbal?.document.doc.fileExtension as string) })
                  )
                );
              });
            this.meetingRepository.setNested('dto', 'participants', response.participants || []);
          },
          error: (error: any) => {
            this.loading = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error.error.message || this.translate.instant('meeting.message.error.loading'),
            });
          },
        });
      }
    }
  }
}
